import { useEffect, useState, type ReactNode } from 'react'

import { fetchTransportOptions, type TransportOptions } from '../api/transport'
import { PageBand, PageShell } from '../components/PageShell'
import { EMERGENCY_NUMBER } from '../lib/emergencyNumber'
import type { CarriedTrip } from './transport/booking'
import { clearCarried, readCarried } from './transport/carry'
import { HoldingPage } from './transport/HoldingPage'
import { readStopped, writeStopped } from './transport/stop'
import { StopScreen } from './transport/StopScreen'
import { TransportFlow } from './transport/TransportFlow'
import { TransportHeader } from './transport/TransportHeader'

/**
 * `/transport` (M34-T03, M39-T09, ADR-0063, ADR-0066). The public options are
 * the switch: while booking is off they answer 404 and this is the M34 holding
 * page, unchanged; once they answer, it is the booking flow. Any failure or a
 * slow answer also gives the holding page, whose phone hand-over always works.
 * The flow is built in M39 (Screens 1–5), M40 adds Screen 6 and the staff
 * desk, and M41 connects payment and switches booking on.
 *
 * The safety check's stop is decided here, before the options answer, so a
 * panicked repeat visit lands on the stop at once (owner ruling Q4). A carried
 * stop also offers "Start a new, non-emergency booking", for the person who
 * pressed Yes by mistake.
 */

/** After this, the holding page: someone may be waiting to call the desk. */
const OPTIONS_TIMEOUT_MS = 5000

type Options =
  | { kind: 'loading' }
  | { kind: 'off' }
  | { kind: 'on'; options: TransportOptions }

function useTransportOptions(): Options {
  const [state, setState] = useState<Options>({ kind: 'loading' })
  useEffect(() => {
    const ctrl = new AbortController()
    let settled = false
    const settle = (next: Options) => {
      if (settled) return
      settled = true
      setState(next)
    }
    const timer = setTimeout(() => {
      settle({ kind: 'off' })
      ctrl.abort()
    }, OPTIONS_TIMEOUT_MS)
    fetchTransportOptions(ctrl.signal).then(
      (options) => settle({ kind: 'on', options }),
      () => settle({ kind: 'off' }),
    )
    return () => {
      settled = true
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [])
  return state
}

export function Transport(): ReactNode {
  // 'fresh' = stopped by a tap on this page; 'carried' = a stop carried over
  // from earlier in this tab.
  const [stop, setStop] = useState<'none' | 'fresh' | 'carried'>(() =>
    readStopped() ? 'carried' : 'none',
  )
  // Focus the gate's heading after a restart, but not on first arrival.
  const [restarted, setRestarted] = useState(false)
  // Or when focus was inside the loading view: it vanishes when the options
  // answer, and focus would otherwise fall to <body> unannounced.
  const [focusedWhileLoading, setFocusedWhileLoading] = useState(false)
  // "Book the same trip again" from the booking page (M40-T11): taken from
  // memory the moment /transport opens and cleared at once, whatever this
  // page then shows (the holding page, the stop, a Back while loading), so it
  // can never fill a later visit in this tab.
  const [carried] = useState<CarriedTrip | null>(readCarried)
  useEffect(() => clearCarried(), [])
  const options = useTransportOptions()
  // A carried trip arrives by a tap on another page: Screen 1's heading takes
  // focus, which would otherwise fall to <body>.
  const focusHeading = restarted || focusedWhileLoading || carried !== null

  const stopBooking = () => {
    writeStopped(true)
    setStop('fresh')
  }

  if (stop !== 'none') {
    return (
      <StopScreen
        offerRestart={stop === 'carried'}
        onRestart={() => {
          writeStopped(false)
          setRestarted(true)
          setStop('none')
        }}
      />
    )
  }
  if (options.kind === 'loading') {
    return (
      <div className="contents" onFocus={() => setFocusedWhileLoading(true)}>
        <Loading onStop={stopBooking} />
      </div>
    )
  }
  if (options.kind === 'off') {
    return <HoldingPage focusHeading={focusHeading} onStop={stopBooking} />
  }
  return (
    <TransportFlow
      options={options.options}
      focusHeading={focusHeading}
      onStop={stopBooking}
      carried={carried}
    />
  )
}

/**
 * The moment before the options answer. 112 is offered from the first paint,
 * but not the gate's big "Yes" button: the progress bar would push it down
 * under a thumb as the flow arrives.
 */
function Loading({ onStop }: { onStop: () => void }): ReactNode {
  return (
    <PageShell measure={640} as="main">
      <TransportHeader />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1">
          Book transport
        </h1>
        <p role="status" className="text-[13px] text-ink-3">
          Loading…
        </p>
        <p className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12px] leading-relaxed text-ink-1">
          Medical emergency? Call{' '}
          <a
            href={`tel:${EMERGENCY_NUMBER}`}
            onClick={onStop}
            onAuxClick={onStop}
            className="font-semibold text-sos underline underline-offset-2"
          >
            {EMERGENCY_NUMBER}
          </a>{' '}
          — do not wait for a booking.
        </p>
      </PageBand>
    </PageShell>
  )
}
